<script setup lang="ts">
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import ImportCandidatesDialog from '@/views/recruiting/ImportCandidatesDialog.vue'
import IssueLinkDialog from '@/views/recruiting/IssueLinkDialog.vue'
import { Modules } from '@/types/generated'
import { formatDateToMonthShort } from '@core/utils/formatters'
import { getDateLocale } from '@core/utils/locale'
import CandidateRowData = Modules.Recruiting.Data.CandidateRowData
import CandidateStatusData = Modules.Recruiting.Data.CandidateStatusData
import CandidateInviteStatusEnum = Modules.Recruiting.Enums.CandidateInviteStatusEnum
import CandidateLinkStatusEnum = Modules.Recruiting.Enums.CandidateLinkStatusEnum
import GameStatusSummaryData = Modules.Game.GameStatusSummaryData
import GameStatusEnum = Modules.Shared.Enums.GameStatusEnum
import VacancyData = Modules.Recruiting.Data.VacancyData

definePage({
  meta: {
    action: 'read',
    subject: 'games',
  },
})

interface StatusOption {
  id: string
  title: string
}

const route = useRoute('recruiting-id')
const router = useRouter()
const { t } = useI18n()
const { locale } = useI18n({ useScope: 'global' })
const dateLocale = getDateLocale(locale.value)
const userData = useCookie<any>('userData')

const userTimeZone = computed(
  () => userData.value?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC',
)

const orgSettings = useCookie<any>('organizationSettings')
if (!(orgSettings.value?.enabledFeatures ?? []).includes('recruiting'))
  await router.replace('/games/list')

const vacancyId = String(route.params.id)

const vacancy = ref<VacancyData | null>(null)
const rows = ref<CandidateRowData[]>([])
const total = ref(0)
const page = ref(1)
const itemsPerPage = ref(25)
const isLoading = ref(true)
const isNotFound = ref(false)
const loadFailed = ref(false)
const loadErrorMessage = ref('')

// Названия статусов игры отдаёт бэкенд (`/games/statuses`) — те же подписи, что в списке игр,
// без второй копии переводов на фронте.
const gameStatuses = ref<StatusOption[]>([])

const isSnackbarVisible = ref(false)
const snackbarText = ref('')
const snackbarColor = ref('success')

const isIssueDialogVisible = ref(false)
const isImportDialogVisible = ref(false)
const isConfirmRevokeVisible = ref(false)
const isConfirmExtraAttemptVisible = ref(false)
const activeLinkId = ref<string | null>(null)

const headers = [
  { title: t('recruiting-page.candidates.headers.candidate'), key: 'candidate', sortable: false },
  { title: t('recruiting-page.candidates.headers.link_status'), key: 'linkStatus', sortable: false },
  { title: t('recruiting-page.candidates.headers.invite'), key: 'invite', sortable: false },
  { title: t('recruiting-page.candidates.headers.game_status'), key: 'gameStatus', sortable: false },
  { title: t('recruiting-page.candidates.headers.attempts'), key: 'attempts', sortable: false },
  { title: t('recruiting-page.candidates.headers.expires_at'), key: 'expiresAt', sortable: false },
  { title: t('recruiting-page.candidates.headers.actions'), key: 'actions', sortable: false },
]

const notify = (text: string, color: string = 'success') => {
  snackbarText.value = text
  snackbarColor.value = color
  isSnackbarVisible.value = true
}

// Продуктовые отказы модуля — 422 с машинным `code` и готовым текстом в `message`; его и
// показываем. Всё остальное (404 на чужую ссылку, 403 выключенной фичи, 5xx) отдаёт служебные
// тексты Laravel — их пользователю показывать нечего, отсюда `fallback` вместо них.
const refusalText = (error: any, fallback: string = t('recruiting-page.messages.error')): string => {
  const status = error?.statusCode ?? error?.response?.status

  if (status !== 422)
    return fallback

  return error?.data?.message ?? error?.data?.error ?? fallback
}

const linkStatusLabel = (status: string): string => t(`recruiting-page.candidates.link_status.${status}`)

const linkStatusColor = (status: string): string => {
  if (status === CandidateLinkStatusEnum.Completed)
    return 'success'
  if (status === CandidateLinkStatusEnum.Revoked)
    return 'error'
  if (status === CandidateLinkStatusEnum.InProgress)
    return 'info'
  if (status === CandidateLinkStatusEnum.Opened)
    return 'warning'

  return 'secondary'
}

const gameStatusLabel = (status: string): string =>
  gameStatuses.value.find(option => option.id === status)?.title ?? status

const gameStatusColor = (status: string): string => {
  if (status === GameStatusEnum.COMPLETED)
    return 'success'
  if (status === GameStatusEnum.FAILED)
    return 'error'
  if (status === GameStatusEnum.MISSED)
    return 'secondary'
  if (status === GameStatusEnum.IN_PROGRESS)
    return 'info'

  return 'warning'
}

// `games` приезжает объектом с числовыми ключами (особенность генератора TS для `list<...>`),
// а не массивом — поэтому `Object.values`, а не `.map()` по самому полю.
const gamesOf = (row: CandidateRowData): GameStatusSummaryData[] =>
  Object.values(row.games ?? {}) as GameStatusSummaryData[]

const isGameFinished = (game: GameStatusSummaryData): boolean =>
  game.status === GameStatusEnum.COMPLETED || game.status === GameStatusEnum.FAILED

const lastFinishedGame = (row: CandidateRowData): GameStatusSummaryData | null => {
  const finished = gamesOf(row).filter(isGameFinished)

  return finished.length ? finished[finished.length - 1] : null
}

const canRevoke = (row: CandidateRowData): boolean =>
  row.candidate.status !== CandidateLinkStatusEnum.Revoked

const canGrantExtraAttempt = (row: CandidateRowData): boolean =>
  row.candidate.status !== CandidateLinkStatusEnum.Revoked && !row.candidate.isExpired

const formatDate = (value: string | null): string =>
  value ? formatDateToMonthShort(value, false, dateLocale, userTimeZone.value) : '—'

const inviteStatusLabel = (status: string): string => t(`recruiting-page.candidates.invite_status.${status}`)

const inviteStatusColor = (status: string): string => {
  if (status === CandidateInviteStatusEnum.Sent)
    return 'success'
  if (status === CandidateInviteStatusEnum.Failed)
    return 'error'
  if (status === CandidateInviteStatusEnum.Queued)
    return 'info'

  return 'secondary'
}

const inviteDetails = (candidate: CandidateStatusData): string[] => {
  if (candidate.inviteStatus === CandidateInviteStatusEnum.Sent && candidate.inviteSentAt)
    return [t('recruiting-page.candidates.invite_sent_at', { date: formatDate(candidate.inviteSentAt) })]

  if (candidate.inviteStatus !== CandidateInviteStatusEnum.Failed)
    return []

  const reason = candidate.inviteFailureReason
    ? t(`recruiting-page.candidates.invite_failure.${candidate.inviteFailureReason}`)
    : null

  return [reason, candidate.inviteFailureDetail].filter((line): line is string => Boolean(line))
}

const fetchVacancy = async () => {
  try {
    vacancy.value = await $api<VacancyData>(`/recruiting/vacancies/${vacancyId}`)
  }
  catch {
    isNotFound.value = true
  }
}

const fetchCandidates = async () => {
  isLoading.value = true
  loadFailed.value = false
  try {
    const response = await $api<{ data: CandidateRowData[]; total: number }>(
      `/recruiting/vacancies/${vacancyId}/candidates`,
      { params: { page: page.value, per_page: itemsPerPage.value } },
    )

    rows.value = response?.data ?? []
    total.value = response?.total ?? 0
  }
  catch (error: any) {
    rows.value = []
    total.value = 0
    loadFailed.value = true
    loadErrorMessage.value = refusalText(error, t('recruiting-page.candidates.load_error'))
  }
  finally {
    isLoading.value = false
  }
}

const fetchGameStatuses = async () => {
  try {
    gameStatuses.value = (await $api<StatusOption[]>('/games/statuses')) ?? []
  }
  catch {
    gameStatuses.value = []
  }
}

const updateOptions = (options: { page: number; itemsPerPage: number }) => {
  page.value = options.page
  itemsPerPage.value = options.itemsPerPage
}

const askRevoke = (row: CandidateRowData) => {
  activeLinkId.value = String(row.candidate.linkId)
  isConfirmRevokeVisible.value = true
}

const askExtraAttempt = (row: CandidateRowData) => {
  activeLinkId.value = String(row.candidate.linkId)
  isConfirmExtraAttemptVisible.value = true
}

const confirmRevoke = async (confirmed: boolean) => {
  isConfirmRevokeVisible.value = false
  if (!confirmed || !activeLinkId.value)
    return

  try {
    await $api(`/recruiting/links/${activeLinkId.value}`, {
      method: 'DELETE',
      headers: { Accept: 'application/json' },
    })
    notify(t('recruiting-page.messages.revoked'))
    await fetchCandidates()
  }
  catch (error: any) {
    notify(refusalText(error), 'error')
  }
}

const confirmExtraAttempt = async (confirmed: boolean) => {
  isConfirmExtraAttemptVisible.value = false
  if (!confirmed || !activeLinkId.value)
    return

  try {
    await $api(`/recruiting/links/${activeLinkId.value}/extra-attempt`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
    })
    notify(t('recruiting-page.messages.attempt_granted'))
    await fetchCandidates()
  }
  catch (error: any) {
    notify(refusalText(error), 'error')
  }
}

watch([page, itemsPerPage], () => {
  fetchCandidates()
})

await Promise.all([fetchVacancy(), fetchCandidates(), fetchGameStatuses()])
</script>

<template>
  <div>
    <div class="d-flex justify-space-between align-center flex-wrap gap-4 mb-4">
      <div>
        <VBtn
          variant="text"
          density="comfortable"
          prepend-icon="tabler-arrow-left"
          class="mb-1 ps-0"
          :to="{ name: 'recruiting' }"
        >
          {{ t('recruiting-page.back') }}
        </VBtn>
        <h4 class="text-h4">
          {{ vacancy?.title ?? t('recruiting-page.candidates.title') }}
        </h4>
      </div>
      <div
        v-if="!isNotFound"
        class="d-flex align-center flex-wrap gap-3"
      >
        <VBtn
          variant="tonal"
          color="secondary"
          prepend-icon="tabler-file-upload"
          @click="isImportDialogVisible = true"
        >
          {{ t('recruiting-page.candidates.import') }}
        </VBtn>
        <VBtn
          prepend-icon="tabler-link-plus"
          @click="isIssueDialogVisible = true"
        >
          {{ t('recruiting-page.candidates.issue') }}
        </VBtn>
      </div>
    </div>

    <VAlert
      v-if="isNotFound"
      type="error"
      variant="tonal"
    >
      {{ t('404_title') }}
    </VAlert>

    <VCard v-else>
      <VAlert
        v-if="loadFailed"
        type="error"
        variant="tonal"
        class="ma-4"
      >
        <div class="d-flex align-center justify-space-between flex-wrap gap-3">
          <span>{{ loadErrorMessage }}</span>
          <VBtn
            size="small"
            variant="tonal"
            color="error"
            @click="fetchCandidates"
          >
            {{ t('recruiting-page.retry') }}
          </VBtn>
        </div>
      </VAlert>

      <VDataTableServer
        v-else
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :headers="headers"
        :items="rows"
        :items-length="total"
        :loading="isLoading"
        :loading-text="t('loading')"
        :items-per-page-options="[10, 25, 50, 100]"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <template #item.candidate="{ item }">
          <div class="d-flex flex-column">
            <span class="font-weight-medium">
              {{ item.candidate.candidateName }} {{ item.candidate.candidateLastName }}
            </span>
            <span
              v-if="item.candidate.candidateEmail || item.candidate.candidatePhone"
              class="text-caption text-disabled"
            >
              {{ [item.candidate.candidateEmail, item.candidate.candidatePhone].filter(Boolean).join(' · ') }}
            </span>
          </div>
        </template>

        <template #item.linkStatus="{ item }">
          <div class="d-flex align-center gap-1">
            <VChip
              :color="linkStatusColor(item.candidate.status)"
              size="small"
            >
              {{ linkStatusLabel(item.candidate.status) }}
            </VChip>
            <VChip
              v-if="item.candidate.isExpired"
              color="warning"
              variant="tonal"
              size="x-small"
            >
              {{ t('recruiting-page.candidates.expired') }}
            </VChip>
          </div>
        </template>

        <template #item.invite="{ item }">
          <VChip
            :color="inviteStatusColor(item.candidate.inviteStatus)"
            :append-icon="inviteDetails(item.candidate).length ? 'tabler-info-circle' : undefined"
            size="small"
          >
            {{ inviteStatusLabel(item.candidate.inviteStatus) }}
            <VTooltip
              v-if="inviteDetails(item.candidate).length"
              location="top"
              activator="parent"
            >
              <div
                v-for="(line, index) in inviteDetails(item.candidate)"
                :key="index"
              >
                {{ line }}
              </div>
            </VTooltip>
          </VChip>
        </template>

        <template #item.gameStatus="{ item }">
          <div
            v-if="gamesOf(item).length"
            class="d-flex align-center flex-wrap gap-1"
          >
            <VChip
              v-for="game in gamesOf(item)"
              :key="String(game.id)"
              :color="gameStatusColor(game.status)"
              size="small"
              :variant="game.hasEvaluation ? 'elevated' : 'tonal'"
              :to="isGameFinished(game) ? { name: 'games-detail-id', params: { id: String(game.id) } } : undefined"
            >
              #{{ game.number }} · {{ gameStatusLabel(game.status) }}
            </VChip>
          </div>
          <span
            v-else
            class="text-disabled"
          >
            {{ t('recruiting-page.candidates.no_games') }}
          </span>
        </template>

        <template #item.attempts="{ item }">
          {{ t('recruiting-page.candidates.attempts', { used: item.candidate.attemptsUsed, allowed: item.candidate.attemptsAllowed }) }}
        </template>

        <template #item.expiresAt="{ item }">
          {{ formatDate(item.candidate.expiresAt) }}
        </template>

        <template #item.actions="{ item }">
          <div class="d-flex justify-end">
            <IconBtn
              v-if="lastFinishedGame(item)"
              :to="{ name: 'games-detail-id', params: { id: String(lastFinishedGame(item)!.id) } }"
            >
              <VTooltip
                location="top"
                activator="parent"
              >
                {{ t('recruiting-page.candidates.open_result') }}
              </VTooltip>
              <VIcon icon="tabler-eye" />
            </IconBtn>
            <IconBtn
              v-if="canGrantExtraAttempt(item)"
              @click="askExtraAttempt(item)"
            >
              <VTooltip
                location="top"
                activator="parent"
              >
                {{ t('recruiting-page.candidates.extra_attempt') }}
              </VTooltip>
              <VIcon
                color="warning"
                icon="tabler-repeat"
              />
            </IconBtn>
            <IconBtn
              v-if="canRevoke(item)"
              @click="askRevoke(item)"
            >
              <VTooltip
                location="top"
                activator="parent"
              >
                {{ t('recruiting-page.candidates.revoke') }}
              </VTooltip>
              <VIcon
                color="error"
                icon="tabler-link-off"
              />
            </IconBtn>
          </div>
        </template>

        <template #no-data>
          <div class="text-center text-disabled py-6">
            {{ t('recruiting-page.candidates.empty') }}
          </div>
        </template>
      </VDataTableServer>
    </VCard>

    <IssueLinkDialog
      v-model:is-dialog-visible="isIssueDialogVisible"
      :vacancy-id="vacancyId"
      @submit="fetchCandidates"
    />

    <ImportCandidatesDialog
      v-model:is-dialog-visible="isImportDialogVisible"
      :vacancy-id="vacancyId"
      @submit="fetchCandidates"
    />

    <ConfirmDialog
      v-model:is-dialog-visible="isConfirmRevokeVisible"
      :confirmation-question="t('recruiting-page.candidates.confirm_revoke')"
      confirm-title=""
      confirm-msg=""
      cancel-title=""
      cancel-msg=""
      @confirm="confirmRevoke"
    />

    <ConfirmDialog
      v-model:is-dialog-visible="isConfirmExtraAttemptVisible"
      :confirmation-question="t('recruiting-page.candidates.confirm_extra_attempt')"
      confirm-title=""
      confirm-msg=""
      cancel-title=""
      cancel-msg=""
      @confirm="confirmExtraAttempt"
    />

    <VSnackbar
      v-model="isSnackbarVisible"
      location="top end"
      :color="snackbarColor"
    >
      {{ snackbarText }}
    </VSnackbar>
  </div>
</template>
